"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { Search, User, Phone, GraduationCap, X, UserPlus, ListTodo, FileText, Kanban, Sparkles, ArrowRight } from "@/lib/icons"
import { useAuth } from "@/context/auth-context"
import { getLeads } from "@/lib/supabase"
import { LEAD_STAGE_META, normalizeLeadStage } from "@/lib/lead-stages"
import { NIVEL_LABELS } from "@/lib/educational-levels"

const ACCIONES = [
  {
    id: "new-lead",
    titulo: "Registrar prospecto nuevo",
    subtitulo: "Crea un prospecto manualmente (Ctrl+N)",
    icon: UserPlus,
    accent: "#10b981",
    event: "openNewLead",
  },
  {
    id: "tareas",
    titulo: "Ver mis tareas",
    subtitulo: "Abre el panel de tareas pendientes (Ctrl+T)",
    icon: ListTodo,
    accent: "#f59e0b",
    event: "openTasksPanel",
  },
  {
    id: "base-datos",
    titulo: "Base de datos",
    subtitulo: "Tabla completa con filtros y export",
    icon: FileText,
    accent: "#6366f1",
    route: "/leads",
  },
  {
    id: "kanban",
    titulo: "Tablero Kanban",
    subtitulo: "Vista por etapas del pipeline",
    icon: Kanban,
    accent: "#7a1b33",
    route: "/kanban",
  },
]

export function CommandPalette({ open, onClose }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [query, setQuery] = useState("")
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)

  // Cargar leads al abrir — usa getLeads que respeta el scope del rol
  // (un asesor solo ve los suyos, super admin ve todos)
  useEffect(() => {
    if (!open || !user) return
    let active = true
    setLoading(true)
    ;(async () => {
      try {
        const { data } = await getLeads(user.email, { limit: 1000 })
        if (active) setLeads(data || [])
      } catch (err) {
        console.warn("[CommandPalette] load:", err?.message)
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [open, user])

  // Focus al abrir
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setQuery("")
      setActiveIndex(0)
    }
  }, [open])

  // Filtrado de leads
  const filteredLeads = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return leads.slice(0, 8)
    return leads
      .filter((l) => {
        const hay = `${l.nombre_tutor || ""} ${l.nombre_alumno || ""} ${l.telefono || ""} ${l.email || ""} ${l.wa_jid || ""}`.toLowerCase()
        return hay.includes(term)
      })
      .slice(0, 15)
  }, [query, leads])

  // Acciones filtradas
  const filteredAcciones = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return ACCIONES
    return ACCIONES.filter((a) =>
      `${a.titulo} ${a.subtitulo}`.toLowerCase().includes(term)
    )
  }, [query])

  // Lista unificada: acciones + leads
  const allItems = useMemo(
    () => [
      ...filteredAcciones.map((a) => ({ kind: "accion", data: a })),
      ...filteredLeads.map((l) => ({ kind: "lead", data: l })),
    ],
    [filteredAcciones, filteredLeads],
  )

  // Reset index cuando cambia la lista
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  const handleSelect = useCallback(
    (item) => {
      if (!item) return
      if (item.kind === "accion") {
        const a = item.data
        if (a.route) navigate(a.route)
        if (a.event) {
          // Pequeño delay para que onClose termine antes del dispatch
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent(a.event))
          }, 50)
        }
        onClose()
        return
      }
      if (item.kind === "lead") {
        const l = item.data
        try {
          localStorage.setItem("open_conversation_lead", l.id)
          localStorage.setItem("highlight_lead", l.id)
          window.dispatchEvent(new CustomEvent("openLeadChat", { detail: { leadId: l.id } }))
        } catch {}
        navigate("/kanban")
        onClose()
      }
    },
    [navigate, onClose],
  )

  // Navegación por teclado
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, allItems.length - 1))
        return
      }
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === "Enter") {
        e.preventDefault()
        handleSelect(allItems[activeIndex])
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, allItems, activeIndex, handleSelect, onClose])

  // Scroll al item activo
  useEffect(() => {
    const el = document.querySelector(`[data-command-index="${activeIndex}"]`)
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [activeIndex])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 sm:pt-[12vh] bg-black/50 backdrop-blur-sm animate-in fade-in-0 duration-150"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95 slide-in-from-top-4 duration-200">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 dark:border-slate-800">
          <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar prospectos por nombre, teléfono o email…"
            className="flex-1 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-400 bg-slate-50 dark:bg-slate-800">
            ESC
          </kbd>
          <button
            onClick={onClose}
            className="sm:hidden p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Results list */}
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {loading && (
            <div className="flex items-center justify-center py-8 text-xs text-slate-400">
              Cargando…
            </div>
          )}

          {!loading && allItems.length === 0 && (
            <div className="flex flex-col items-center py-10 text-center">
              <Search className="h-6 w-6 text-slate-300 dark:text-slate-700 mb-2" />
              <p className="text-xs text-slate-500">Sin resultados para "{query}"</p>
              <p className="text-[10px] text-slate-400 mt-1">
                Prueba con otro nombre, teléfono o parte del email.
              </p>
            </div>
          )}

          {/* Acciones rápidas */}
          {!loading && filteredAcciones.length > 0 && (
            <>
              <div className="px-4 py-1.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  Acciones rápidas
                </p>
              </div>
              {filteredAcciones.map((accion, i) => {
                const globalIdx = i
                const Icon = accion.icon
                const isActive = activeIndex === globalIdx
                return (
                  <button
                    key={accion.id}
                    data-command-index={globalIdx}
                    type="button"
                    onMouseEnter={() => setActiveIndex(globalIdx)}
                    onClick={() => handleSelect({ kind: "accion", data: accion })}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      isActive ? "bg-slate-100 dark:bg-slate-800" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    }`}
                  >
                    <div
                      className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0"
                      style={{ backgroundColor: `${accion.accent}15`, color: accion.accent }}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {accion.titulo}
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                        {accion.subtitulo}
                      </p>
                    </div>
                    {isActive && <ArrowRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />}
                  </button>
                )
              })}
            </>
          )}

          {/* Leads */}
          {!loading && filteredLeads.length > 0 && (
            <>
              <div className="px-4 py-1.5 mt-1">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  Prospectos {query && `· ${filteredLeads.length} resultado${filteredLeads.length !== 1 ? "s" : ""}`}
                </p>
              </div>
              {filteredLeads.map((lead, i) => {
                const globalIdx = filteredAcciones.length + i
                const isActive = activeIndex === globalIdx
                const stage = normalizeLeadStage(lead.estado_lead)
                const stageMeta = LEAD_STAGE_META[stage] || {}
                const stageTitle = stageMeta.title || lead.estado_lead || "—"
                const stageAccent = stageMeta.accent || "#94a3b8"
                const nivelLabel = NIVEL_LABELS[(lead.nivel_educativo || "").toLowerCase()] || lead.nivel_educativo || ""
                return (
                  <button
                    key={lead.id}
                    data-command-index={globalIdx}
                    type="button"
                    onMouseEnter={() => setActiveIndex(globalIdx)}
                    onClick={() => handleSelect({ kind: "lead", data: lead })}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      isActive ? "bg-slate-100 dark:bg-slate-800" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    }`}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 font-bold text-[11px] flex-shrink-0">
                      {(lead.nombre_tutor || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">
                          {lead.nombre_tutor || "Sin nombre"}
                        </p>
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold text-white flex-shrink-0"
                          style={{ backgroundColor: stageAccent }}
                        >
                          {stageTitle}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[10.5px] text-slate-500 dark:text-slate-400">
                        {lead.telefono && (
                          <span className="inline-flex items-center gap-0.5">
                            <Phone className="h-2.5 w-2.5" />
                            {lead.telefono}
                          </span>
                        )}
                        {nivelLabel && (
                          <span className="inline-flex items-center gap-0.5">
                            <GraduationCap className="h-2.5 w-2.5" />
                            {nivelLabel}
                          </span>
                        )}
                        {lead.nombre_alumno && (
                          <span className="truncate">· {lead.nombre_alumno}</span>
                        )}
                      </div>
                    </div>
                    {isActive && <ArrowRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />}
                  </button>
                )
              })}
            </>
          )}
        </div>

        {/* Footer con keyboard hints */}
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 text-[9.5px] text-slate-400">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <kbd className="font-mono px-1 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">↑↓</kbd>
              navegar
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="font-mono px-1 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">↵</kbd>
              abrir
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="font-mono px-1 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">esc</kbd>
              cerrar
            </span>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> CRM Tepeyac
          </span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
